import React, { useState, useEffect } from 'react';
import { locationsAPI } from '../lib/api';
import { useGMB } from '../contexts/GMBContext';
import { MapPin, Star, RefreshCw, CheckCircle2, Shield, Building2, Zap, MoreVertical, Search, LayoutGrid, List } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const LocationsPage = () => {
  const { isConnected, connectGMB, loading: gmbLoading } = useGMB();
  const [locations, setLocations] = useState([]);
  const [activatedCount, setActivatedCount] = useState(0);
  const [maxLocations, setMaxLocations] = useState(1);
  const [planName, setPlanName] = useState('free');
  const [gmbConnected, setGmbConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => { fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      const res = await locationsAPI.getAll();
      const d = res.data;
      setLocations(d.locations || []);
      setActivatedCount(d.activated_count || 0);
      setMaxLocations(d.max_locations || 1);
      setPlanName(d.plan_name || 'free');
      setGmbConnected(d.gmb_connected || false);
    } catch { toast.error('Failed to load locations'); }
    finally { setLoading(false); }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await locationsAPI.refresh();
      toast.success('Locations refreshed from Google!');
      await fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to refresh locations');
    } finally { setRefreshing(false); }
  };

  const handleActivate = async (gmbLocationId) => {
    try {
      await locationsAPI.activate(gmbLocationId);
      toast.success('Location activated!');
      fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate');
    }
  };

  const handleDeactivate = async (gmbLocationId) => {
    try {
      await locationsAPI.deactivate(gmbLocationId);
      toast.success('Location deactivated');
      fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const handleSetPrimary = async (gmbLocationId) => {
    try {
      await locationsAPI.setPrimary(gmbLocationId);
      toast.success('Primary location updated');
      fetchLocations();
    } catch { toast.error('Failed to set primary'); }
  };

  const filteredLocations = locations.filter(loc =>
    loc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    loc.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    loc.address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeLocations = locations.filter(l => l.is_active);

  if (loading || gmbLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
    </div>
  );

  // Not connected to GMB
  if (!gmbConnected) {
    return (
      <div className="space-y-6 animate-fade-in-up">
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Business Locations</h1>
          <p className="text-[#5F6368]">Manage your Google Business Profile locations</p>
        </div>
        <div className="dashboard-card text-center py-16">
          <div className="w-20 h-20 bg-[#E8F0FE] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-10 h-10 text-[#1A73E8]" />
          </div>
          <h3 className="text-xl font-semibold text-[#202124] mb-2">Connect Google Business Profile</h3>
          <p className="text-[#5F6368] max-w-md mx-auto mb-6">
            Connect your Google account to fetch and manage your business locations. Your membership plan allows up to <strong>{maxLocations}</strong> active location{maxLocations > 1 ? 's' : ''}.
          </p>
          <button
            onClick={connectGMB}
            className="btn-google inline-flex items-center gap-2 px-6 py-3"
            data-testid="connect-gmb-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Connect with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>My Google Business Profiles</h1>
          <p className="text-[#5F6368]">Manage and activate your business locations</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-outline flex items-center gap-2 text-sm"
            data-testid="refresh-locations-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Syncing...' : 'Sync from Google'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="dashboard-card" data-testid="stat-total-profiles">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E8F0FE] rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1A73E8]" />
            </div>
            <div>
              <p className="text-xs text-[#5F6368] uppercase tracking-wide">Total Profiles</p>
              <p className="text-2xl font-semibold text-[#202124]">{locations.length}</p>
            </div>
          </div>
        </div>
        <div className="dashboard-card" data-testid="stat-active-profiles">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E6F4EA] rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-[#1E8E3E]" />
            </div>
            <div>
              <p className="text-xs text-[#5F6368] uppercase tracking-wide">Active</p>
              <p className="text-2xl font-semibold text-[#1E8E3E]">{activatedCount}</p>
            </div>
          </div>
        </div>
        <div className="dashboard-card" data-testid="stat-plan-limit">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FEF7E0] rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#F9AB00]" />
            </div>
            <div>
              <p className="text-xs text-[#5F6368] uppercase tracking-wide">Plan Limit</p>
              <p className="text-2xl font-semibold text-[#202124]">{activatedCount} / {maxLocations}</p>
            </div>
          </div>
        </div>
        <div className="dashboard-card" data-testid="stat-plan-name">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#F3E8FD] rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#9334E9]" />
            </div>
            <div>
              <p className="text-xs text-[#5F6368] uppercase tracking-wide">Current Plan</p>
              <p className="text-2xl font-semibold text-[#9334E9] capitalize">{planName}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search & View Toggle */}
      <div className="dashboard-card">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-[#F1F3F4] rounded-lg px-4 py-2">
            <Search className="w-4 h-4 text-[#5F6368]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search business profiles..."
              className="bg-transparent outline-none text-sm w-full"
              data-testid="search-locations"
            />
          </div>
          <div className="flex items-center gap-1 border border-[#DADCE0] rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-[#E8F0FE] text-[#1A73E8]' : 'text-[#5F6368] hover:bg-[#F1F3F4]'}`}
              data-testid="view-grid"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-[#E8F0FE] text-[#1A73E8]' : 'text-[#5F6368] hover:bg-[#F1F3F4]'}`}
              data-testid="view-list"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Upgrade notice if at limit */}
      {activatedCount >= maxLocations && locations.length > maxLocations && (
        <div className="bg-[#FEF7E0] border border-[#F9AB00] rounded-xl p-4 flex items-center justify-between">
          <p className="text-sm text-[#202124]">
            You've reached your <strong>{planName}</strong> plan limit of <strong>{maxLocations}</strong> location{maxLocations > 1 ? 's' : ''}. Upgrade to manage more.
          </p>
          <Link to="/pricing" className="btn-google text-sm px-4 py-2" data-testid="upgrade-plan-btn">
            Upgrade Plan
          </Link>
        </div>
      )}

      {/* Empty State */}
      {locations.length === 0 && (
        <div className="dashboard-card text-center py-12">
          <Building2 className="w-16 h-16 mx-auto text-[#DADCE0]" />
          <h3 className="mt-4 text-lg font-semibold text-[#202124]">No locations found</h3>
          <p className="mt-2 text-[#5F6368] max-w-md mx-auto">
            Your Google Business Profile doesn't have any locations yet, or the Business Information API hasn't been approved. Click "Sync from Google" to try fetching locations.
          </p>
        </div>
      )}

      {/* Location Cards */}
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4' : 'space-y-3'}>
        {filteredLocations.map((loc) => (
          <LocationCard
            key={loc.gmb_location_id}
            location={loc}
            canActivate={activatedCount < maxLocations}
            onActivate={handleActivate}
            onDeactivate={handleDeactivate}
            onSetPrimary={handleSetPrimary}
            viewMode={viewMode}
          />
        ))}
      </div>

      {/* Pagination info */}
      {locations.length > 0 && (
        <div className="text-center text-sm text-[#9AA0A6] py-2">
          Showing {filteredLocations.length} of {locations.length} location{locations.length > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

const LocationCard = ({ location, canActivate, onActivate, onDeactivate, onSetPrimary, viewMode }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const loc = location;
  const isActive = loc.is_active;

  const initial = loc.name ? loc.name.charAt(0).toUpperCase() : 'B';
  const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(loc.name || 'Business')}&background=4285F4&color=fff&size=96&bold=true`;

  if (viewMode === 'list') {
    return (
      <div className={`dashboard-card flex items-center gap-4 ${isActive ? 'border-l-4 border-l-[#1A73E8]' : ''}`} data-testid={`location-card-${loc.gmb_location_id}`}>
        <img src={avatarUrl} alt={loc.name} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-[#202124] truncate">{loc.name}</h3>
            {isActive && <span className="text-xs bg-[#E6F4EA] text-[#1E8E3E] px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Active</span>}
            {loc.is_primary && <span className="text-xs bg-[#E8F0FE] text-[#1A73E8] px-2 py-0.5 rounded-full font-medium">Primary</span>}
          </div>
          {loc.category && <p className="text-sm text-[#5F6368]">{loc.category}</p>}
          {loc.address && <p className="text-xs text-[#9AA0A6] truncate">{loc.address}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isActive ? (
            <button onClick={() => onDeactivate(loc.gmb_location_id)} className="btn-outline text-sm px-4 py-2" data-testid={`deactivate-${loc.gmb_location_id}`}>
              Deactivate
            </button>
          ) : (
            <button
              onClick={() => onActivate(loc.gmb_location_id)}
              disabled={!canActivate}
              className={`text-sm px-4 py-2 rounded-lg font-medium ${canActivate ? 'btn-google' : 'bg-[#F1F3F4] text-[#9AA0A6] cursor-not-allowed'}`}
              data-testid={`activate-${loc.gmb_location_id}`}
            >
              Activate
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-card flex flex-col ${isActive ? 'ring-2 ring-[#1A73E8] ring-offset-2' : ''}`} data-testid={`location-card-${loc.gmb_location_id}`}>
      {/* Card Header */}
      <div className="flex items-start gap-3 mb-4">
        <img src={avatarUrl} alt={loc.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-[#202124] text-base">{loc.name}</h3>
            {isActive && (
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4 text-[#1E8E3E]" />
                <Zap className="w-3.5 h-3.5 text-[#F9AB00]" />
              </span>
            )}
          </div>
          {isActive && <p className="text-xs text-[#1E8E3E] font-medium">GBP Connected</p>}
          {!isActive && <p className="text-xs text-[#9AA0A6]">Not managed</p>}
        </div>
        <div className="relative">
          {isActive && (
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-1.5 text-[#5F6368] hover:bg-[#F1F3F4] rounded-lg" data-testid={`menu-${loc.gmb_location_id}`}>
              <MoreVertical className="w-4 h-4" />
            </button>
          )}
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-8 z-20 bg-white rounded-lg shadow-lg border border-[#DADCE0] py-1 w-40">
                {!loc.is_primary && (
                  <button onClick={() => { onSetPrimary(loc.gmb_location_id); setMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-[#202124] hover:bg-[#F1F3F4]">
                    Set as Primary
                  </button>
                )}
                <button onClick={() => { onDeactivate(loc.gmb_location_id); setMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-[#D93025] hover:bg-[#FCE8E6]">
                  Deactivate
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Address */}
      <div className="flex-1">
        {loc.address && (
          <div className="flex items-start gap-2 mb-2">
            <MapPin className="w-4 h-4 text-[#9AA0A6] mt-0.5 flex-shrink-0" />
            <p className="text-sm text-[#5F6368] line-clamp-2">{loc.address}{loc.city ? `, ${loc.city}` : ''}</p>
          </div>
        )}
        {loc.category && (
          <p className="text-xs text-[#9AA0A6] mb-2">{loc.category}</p>
        )}
        {loc.is_primary && (
          <span className="inline-block text-xs bg-[#E8F0FE] text-[#1A73E8] px-2 py-0.5 rounded-full font-medium mb-3">
            <Star className="w-3 h-3 inline mr-1" />Primary
          </span>
        )}
      </div>

      {/* Action Button */}
      <div className="mt-auto pt-3 border-t border-[#F1F3F4]">
        {isActive ? (
          <button
            onClick={() => onDeactivate(loc.gmb_location_id)}
            className="w-full py-2.5 rounded-lg text-sm font-medium bg-[#202124] text-white hover:bg-[#3C4043] transition-colors"
            data-testid={`manage-${loc.gmb_location_id}`}
          >
            Manage
          </button>
        ) : (
          <button
            onClick={() => onActivate(loc.gmb_location_id)}
            disabled={!canActivate}
            className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${canActivate ? 'bg-[#1A73E8] text-white hover:bg-[#1557B0]' : 'bg-[#F1F3F4] text-[#9AA0A6] cursor-not-allowed'}`}
            data-testid={`activate-${loc.gmb_location_id}`}
          >
            {canActivate ? 'Activate Location' : 'Upgrade to Activate'}
          </button>
        )}
      </div>
    </div>
  );
};

export default LocationsPage;
